/* Admin — Campaign Manager.
 *
 * One tab for every kind of fundraising campaign: Crisis Response,
 * Fundraiser, Supply Drive, Program Initiative, Event, and any custom
 * type the admin adds. Runs against /api/admin/donation-campaigns
 * which operates on the same MongoDB collection as the legacy Crisis
 * Response system — every existing crisis campaign, donation link,
 * distribution log, and transparency report is preserved.
 *
 * List view features:
 *   - Filter by type + active + public
 *   - Toggle active / public inline
 *   - Copy donation link + view/download branded QR
 *   - Live raised / donors / distributed metrics
 *
 * Edit modal features:
 *   - Type selector (dropdown of admin-managed types)
 *   - Name, goal, description, media (image or video)
 *   - Optional country program + location
 *   - Active + public + live impact toggles
 *
 * Types manager:
 *   - Small inline modal to add/remove custom types
 *   - System types (crisis_response) cannot be deleted
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, Copy, Check, Download, QrCode, Eye, EyeOff, Power,
  X, ExternalLink, Filter, Tag, RefreshCw, Loader2, Users, DollarSign,
  Wallet, Package, AlertTriangle, Archive, ArchiveRestore, ShieldAlert,
  FileText,
} from 'lucide-react';
import MediaField from '../../../components/admin/MediaField';
import CampaignToolsModal from './CampaignToolsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/donation-campaigns`;

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString(); } catch { return v; } };

// ============ Types manager modal =============================
const TypesManager = ({ types, onClose, onChanged }) => {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/types`, { label: label.trim() }, { withCredentials: true });
      setLabel('');
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add type'); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this type? Campaigns using it must be re-typed first.')) return;
    try {
      await axios.delete(`${API}/types/${id}`, { withCredentials: true });
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to remove type'); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="types-manager-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#BA1826]" /> Manage Campaign Types
          </h3>
          <button onClick={onClose} data-testid="types-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#6B7280] mb-3">
          Add your own types (e.g. &ldquo;Back-to-School&rdquo;, &ldquo;Holiday Drive&rdquo;) or delete unused ones.
          System types are locked.
        </p>
        <div className="flex gap-2 mb-3">
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="New type name…"
            className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
            data-testid="new-type-input"
            onKeyDown={(e) => e.key === 'Enter' && add()} />
          <button onClick={add} disabled={busy || !label.trim()}
            className="btn-primary text-sm px-4 disabled:opacity-50"
            data-testid="add-type-btn">Add</button>
        </div>
        <div className="space-y-1.5">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between bg-[#F9FAFB] px-3 py-2 rounded border border-[#E5E7EB]"
                 data-testid={`type-row-${t.id}`}>
              <div>
                <p className="text-sm font-medium text-[#0A0A0A]">{t.label}</p>
                <p className="text-[10px] text-[#9CA3AF]">id: {t.id}{t.system ? ' · locked' : ''}</p>
              </div>
              {!t.system && (
                <button onClick={() => remove(t.id)}
                        className="text-[#B91C1C] hover:bg-red-50 p-1.5 rounded"
                        data-testid={`delete-type-${t.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ Create / Edit modal =============================
const CampaignForm = ({ campaign, types, countryPrograms, onSave, onClose }) => {
  const [f, setF] = useState({
    name: campaign?.name || '',
    type: campaign?.type || (types[0]?.id || 'fundraiser'),
    country_program_id: campaign?.country_program_id || '',
    country_name: campaign?.country_name || '',
    location: campaign?.location || '',
    goal: campaign?.goal || 0,
    description: campaign?.description || '',
    status: campaign?.status || 'active',
    start_date: (campaign?.start_date || new Date().toISOString()).slice(0, 10),
    end_date: (campaign?.end_date || '').slice(0, 10),
    media_url: campaign?.media_url || '',
    additional_photos: Array.isArray(campaign?.additional_photos)
      ? campaign.additional_photos.filter(Boolean).slice(0, 6)
      : [],
    is_active: campaign?.is_active !== false,
    show_publicly: !!campaign?.show_publicly,
    show_live_impact: campaign?.show_live_impact !== false,
  });
  const [saving, setSaving] = useState(false);
  const inputC = 'w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm';

  const onPickCountry = (id) => {
    const cp = countryPrograms.find((c) => c.id === id);
    setF({
      ...f, country_program_id: id,
      country_name: cp?.country_name || f.country_name,
      location: cp?.program_city || f.location,
    });
  };

  const submit = async () => {
    if (!f.name.trim()) { alert('Campaign name is required.'); return; }
    setSaving(true);
    await onSave({
      ...f,
      goal: parseFloat(f.goal) || 0,
      start_date: f.start_date ? new Date(f.start_date).toISOString() : null,
      end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="campaign-form-modal">
      <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">
            {campaign ? 'Edit Campaign' : 'New Campaign'}
          </h3>
          <button onClick={onClose} data-testid="campaign-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Campaign name *</label>
              <input value={f.name} onChange={(e) => setF({...f, name: e.target.value})}
                placeholder="e.g. Back-to-School 2026"
                className={inputC} data-testid="campaign-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Type *</label>
              <select value={f.type} onChange={(e) => setF({...f, type: e.target.value})}
                className={inputC} data-testid="campaign-type-select">
                {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Goal (USD)</label>
              <input type="number" min="0" step="1" value={f.goal}
                onChange={(e) => setF({...f, goal: e.target.value})}
                className={inputC} data-testid="campaign-goal-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Description</label>
            <textarea rows={3} value={f.description}
              onChange={(e) => setF({...f, description: e.target.value})}
              placeholder="A short story donors will see on the campaign page…"
              className={inputC} data-testid="campaign-description-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Hero image or video (optional)</label>
            <MediaField value={f.media_url}
              onChange={(url) => setF({...f, media_url: url})}
              testIdPrefix="campaign-media" />
          </div>
          {/* Additional campaign photos — up to 6. Kept separate from the
              hero so admins can drop in flyers, prep shots, distribution
              photos, etc. without disturbing the main media. */}
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Additional campaign photos <span className="text-[#9CA3AF]">(optional — up to 6)</span>
            </label>
            <p className="text-[11px] text-[#6B7280] mb-2">
              Add real photos from preparation, supplies, distribution, or impact.
              They&apos;ll appear in a grid below the campaign story on the public page.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="additional-photos-grid">
              {[0, 1, 2, 3, 4, 5].map((idx) => {
                const current = f.additional_photos[idx] || '';
                return (
                  <div key={idx} data-testid={`additional-photo-slot-${idx}`}>
                    <MediaField
                      value={current}
                      onChange={(url) => {
                        const next = [...f.additional_photos];
                        if (url) { next[idx] = url; }
                        else { next.splice(idx, 1); }
                        setF({ ...f, additional_photos: next.filter(Boolean).slice(0, 6) });
                      }}
                      testIdPrefix={`extra-photo-${idx}`}
                      label={`Photo ${idx + 1}`}
                      allowedKinds={['image']} />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Country (optional)</label>
              <select value={f.country_program_id}
                onChange={(e) => onPickCountry(e.target.value)}
                className={inputC} data-testid="campaign-country-select">
                <option value="">— None (global campaign) —</option>
                {countryPrograms.map((c) => (
                  <option key={c.id} value={c.id}>{c.country_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Location / city</label>
              <input value={f.location} onChange={(e) => setF({...f, location: e.target.value})}
                placeholder="Optional — e.g. Bogotá"
                className={inputC} data-testid="campaign-location-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Start date</label>
              <input type="date" value={f.start_date}
                onChange={(e) => setF({...f, start_date: e.target.value})}
                className={inputC} data-testid="campaign-start-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">End date (optional)</label>
              <input type="date" value={f.end_date}
                onChange={(e) => setF({...f, end_date: e.target.value})}
                className={inputC} data-testid="campaign-end-date" />
            </div>
          </div>
          {/* Toggles */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded p-3 space-y-2 text-sm">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.is_active}
                onChange={(e) => setF({...f, is_active: e.target.checked})}
                className="accent-[#BA1826]" data-testid="campaign-active-checkbox" />
              <span><b>Active</b> — donations flow into this campaign. Turn off to freeze intake without deleting.</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.show_publicly}
                onChange={(e) => setF({...f, show_publicly: e.target.checked})}
                className="accent-[#BA1826]" data-testid="campaign-public-checkbox" />
              <span><b>Show publicly</b> — visible on <code>/campaigns</code> and country page (Phase 3).</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.show_live_impact}
                onChange={(e) => setF({...f, show_live_impact: e.target.checked})}
                className="accent-[#BA1826]" data-testid="campaign-live-impact-checkbox" />
              <span><b>Show live impact</b> — display live raised / donors publicly.</span>
            </label>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded text-sm hover:bg-[#F9FAFB]"
                  data-testid="campaign-form-cancel">Cancel</button>
          <button onClick={submit} disabled={saving}
                  className="btn-primary flex-1 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="campaign-form-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {campaign ? 'Save changes' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Donation Link + QR modal =============================
const LinkAndQrModal = ({ campaign, onClose }) => {
  const [qr, setQr] = useState(null);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [qrRes, linkRes] = await Promise.all([
          axios.get(`${API}/${campaign.id}/qr`, { withCredentials: true }),
          axios.get(`${API}/${campaign.id}/donation-link`, { withCredentials: true }),
        ]);
        setQr(qrRes.data);
        setLink(linkRes.data);
      } catch { alert('Failed to load link & QR'); }
      setLoading(false);
    })();
  }, [campaign.id]);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { alert('Could not copy'); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="link-qr-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg">Donation Link &amp; QR</h3>
          <button onClick={onClose} data-testid="link-qr-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">
          Share this link or QR on flyers and socials. Every donation made through it credits
          to <b>{campaign.name}</b>.
        </p>

        {/* Publication readiness warning — the biggest safety net. If the
            campaign isn't publicly visible right now, donors scanning
            this QR / clicking this link will hit "Campaign not available".
            Warn LOUDLY before the admin distributes it. */}
        {qr && qr.readiness && !qr.readiness.is_publicly_visible && (
          <div className="mb-4 p-3 rounded border-2 border-[#B91C1C] bg-[#FEF2F2]"
               data-testid="qr-readiness-warning">
            <p className="text-xs font-bold text-[#B91C1C] mb-1">
              ⚠ Not ready to share yet
            </p>
            <p className="text-[11px] text-[#7F1D1D] leading-relaxed">
              Donors who scan this QR or click the link right now will see
              &ldquo;Campaign not available&rdquo;. Fix before distributing:
            </p>
            <ul className="text-[11px] text-[#7F1D1D] list-disc ml-4 mt-1">
              {qr.readiness.blockers.map((b, i) => (
                <li key={i} data-testid={`qr-blocker-${i}`}>{b}</li>
              ))}
            </ul>
          </div>
        )}
        {qr && qr.readiness && qr.readiness.is_publicly_visible && (
          <div className="mb-4 p-2.5 rounded border border-[#BBF7D0] bg-[#F0FDF4] text-[11px] text-[#166534]"
               data-testid="qr-readiness-ok">
            ✓ Ready to share — Active + Public. Donors will land on the campaign page.
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#BA1826]" />
          </div>
        ) : (
          <>
            {link && (
              <div className="space-y-2 mb-4">
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded p-3">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Campaign page</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs flex-1 truncate text-[#0A0A0A]" data-testid="campaign-page-url">{link.url}</code>
                    <button onClick={() => copyUrl(link.url)}
                            className="p-1.5 rounded border border-[#E5E7EB] hover:bg-white"
                            data-testid="copy-page-url-btn">
                      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <a href={link.url} target="_blank" rel="noopener noreferrer"
                       className="p-1.5 rounded border border-[#E5E7EB] hover:bg-white"
                       data-testid="preview-page-btn">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded p-3">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Direct donate link</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs flex-1 truncate text-[#0A0A0A]" data-testid="direct-donate-url">{link.donate_url}</code>
                    <button onClick={() => copyUrl(link.donate_url)}
                            className="p-1.5 rounded border border-[#E5E7EB] hover:bg-white"
                            data-testid="copy-donate-url-btn">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            {qr && (
              <div className="text-center">
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 inline-block">
                  <img src={qr.qr_image} alt="Campaign QR" className="w-48 h-48"
                       data-testid="campaign-qr-image" />
                </div>
                <div className="mt-3">
                  <a href={qr.qr_image} download={qr.filename}
                     className="btn-primary text-sm inline-flex items-center gap-2"
                     data-testid="download-campaign-qr">
                    <Download className="w-4 h-4" /> Download QR
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============ Row card ================================
const CampaignRow = ({ c, types, onEdit, onDelete, onArchive, onUnarchive,
                       onToggleActive, onTogglePublic, onShowLink, onShowTools }) => {
  const type = types.find((t) => t.id === c.type);
  const pct = c.goal > 0 ? Math.min(100, (c.total_raised / c.goal) * 100) : 0;
  const archived = !!c.is_archived;
  const canHardDelete = !!c.can_hard_delete;
  return (
    <div className={`bg-white border rounded-lg p-4 ${archived ? 'border-[#E5E7EB] opacity-70' : 'border-[#E5E7EB]'}`}
         data-testid={`campaign-row-${c.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-heading font-bold text-sm text-[#0A0A0A] truncate">{c.name}</h3>
            <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#4B5563]"
                  data-testid={`campaign-type-badge-${c.id}`}>
              {type?.label || c.type}
            </span>
            {archived ? (
              <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] inline-flex items-center gap-1"
                    data-testid={`campaign-archived-badge-${c.id}`}>
                <Archive className="w-2.5 h-2.5" /> Archived
              </span>
            ) : c.is_active ? (
              <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]"
                    data-testid={`campaign-active-badge-${c.id}`}>Active</span>
            ) : (
              <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]"
                    data-testid={`campaign-inactive-badge-${c.id}`}>Inactive</span>
            )}
            {!archived && c.show_publicly && (
              <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1E40AF]"
                    data-testid={`campaign-public-badge-${c.id}`}>Public</span>
            )}
          </div>
          <p className="text-[11px] text-[#6B7280]">
            {c.country_name || 'Global'}{c.location ? ` — ${c.location}` : ''} · Started {fmtDate(c.start_date)}
            {c.slug ? <> · <code className="text-[10px]">/{c.slug}</code></> : null}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!archived && (
            <>
              <button onClick={() => onShowLink(c)}
                      title="Donation link & QR"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`show-link-qr-btn-${c.id}`}>
                <QrCode className="w-4 h-4 text-[#BA1826]" />
              </button>
              <button onClick={() => onShowTools(c)}
                      title="Reports, distributions, donors, send transparency report"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`show-tools-btn-${c.id}`}>
                <FileText className="w-4 h-4 text-[#4B5563]" />
              </button>
              <button onClick={() => onTogglePublic(c)}
                      title={c.show_publicly ? 'Hide from public' : 'Show publicly'}
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`toggle-public-btn-${c.id}`}>
                {c.show_publicly ? <Eye className="w-4 h-4 text-[#1E40AF]" /> : <EyeOff className="w-4 h-4 text-[#6B7280]" />}
              </button>
              <button onClick={() => onToggleActive(c)}
                      title={c.is_active ? 'Deactivate (freeze donations)' : 'Activate'}
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`toggle-active-btn-${c.id}`}>
                <Power className={`w-4 h-4 ${c.is_active ? 'text-[#166534]' : 'text-[#6B7280]'}`} />
              </button>
              <button onClick={() => onEdit(c)}
                      title="Edit"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`edit-campaign-btn-${c.id}`}>
                <Edit2 className="w-4 h-4 text-[#4B5563]" />
              </button>
              <button onClick={() => onArchive(c)}
                      title="Archive — hide from public and preserve records"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-amber-50"
                      data-testid={`archive-campaign-btn-${c.id}`}>
                <Archive className="w-4 h-4 text-[#92400E]" />
              </button>
              {/* Permanent delete — only when there is no financial history */}
              <button onClick={() => onDelete(c)}
                      disabled={!canHardDelete}
                      title={canHardDelete
                        ? 'Permanently delete (only allowed because this campaign has no history)'
                        : 'Cannot delete — this campaign has donations, distributions, or reports. Archive instead to preserve records.'}
                      className={`p-1.5 rounded border ${canHardDelete
                        ? 'border-[#FCA5A5] hover:bg-red-50'
                        : 'border-[#E5E7EB] opacity-40 cursor-not-allowed'}`}
                      data-testid={`delete-campaign-btn-${c.id}`}>
                <Trash2 className={`w-4 h-4 ${canHardDelete ? 'text-[#B91C1C]' : 'text-[#9CA3AF]'}`} />
              </button>
            </>
          )}
          {archived && (
            <>
              <button onClick={() => onShowTools(c)}
                      title="View history — reports, distributions, donors"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      data-testid={`show-tools-btn-${c.id}`}>
                <FileText className="w-4 h-4 text-[#4B5563]" />
              </button>
              <button onClick={() => onUnarchive(c)}
                      title="Restore — brings the campaign back (inactive + private until you re-enable)"
                      className="p-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] inline-flex items-center gap-1"
                      data-testid={`unarchive-campaign-btn-${c.id}`}>
                <ArchiveRestore className="w-4 h-4 text-[#166534]" />
                <span className="text-xs">Restore</span>
              </button>
              {canHardDelete && (
                <button onClick={() => onDelete(c)}
                        title="Permanently delete (allowed — no history)"
                        className="p-1.5 rounded border border-[#FCA5A5] hover:bg-red-50"
                        data-testid={`delete-campaign-btn-${c.id}`}>
                  <Trash2 className="w-4 h-4 text-[#B91C1C]" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Progress + Metrics */}
      <div className="mt-3">
        {c.goal > 0 && (
          <div className="relative h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden mb-2">
            <div className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full transition-all"
                 style={{ width: `${pct}%` }}></div>
          </div>
        )}
        <div className="grid grid-cols-4 gap-2 text-[11px]">
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
              <DollarSign className="w-2.5 h-2.5" /> Raised
            </p>
            <p className="font-semibold text-[#0A0A0A]" data-testid={`raised-${c.id}`}>{money(c.total_raised)}</p>
            {c.goal > 0 && <p className="text-[9px] text-[#9CA3AF]">of {money(c.goal)}</p>}
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
              <Users className="w-2.5 h-2.5" /> Donors
            </p>
            <p className="font-semibold text-[#0A0A0A]" data-testid={`donors-${c.id}`}>{c.donor_count || 0}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
              <Package className="w-2.5 h-2.5" /> Distributed
            </p>
            <p className="font-semibold text-[#0A0A0A]" data-testid={`distributed-${c.id}`}>{money(c.distributed_amount)}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
              <Wallet className="w-2.5 h-2.5" /> Balance
            </p>
            <p className="font-semibold text-[#0A0A0A]" data-testid={`balance-${c.id}`}>{money(c.remaining_balance)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Main tab ================================
const CampaignManagerTab = ({ countryPrograms = [] }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showTypes, setShowTypes] = useState(false);
  const [showLinkFor, setShowLinkFor] = useState(null);
  const [showToolsFor, setShowToolsFor] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('');   // '', 'active', 'inactive'
  const [filterPublic, setFilterPublic] = useState('');   // '', 'public', 'private'
  const [showArchived, setShowArchived] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/types`, { withCredentials: true });
      setTypes(r.data.types || []);
    } catch { /* silent */ }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}?include_archived=${showArchived}`,
        { withCredentials: true });
      setCampaigns(r.data.campaigns || []);
    } catch (e) {
      alert('Failed to load campaigns: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [showArchived]);

  useEffect(() => { loadTypes(); loadCampaigns(); }, [loadTypes, loadCampaigns]);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (filterType && c.type !== filterType) return false;
      if (filterActive === 'active' && !c.is_active) return false;
      if (filterActive === 'inactive' && c.is_active) return false;
      if (filterPublic === 'public' && !c.show_publicly) return false;
      if (filterPublic === 'private' && c.show_publicly) return false;
      return true;
    });
  }, [campaigns, filterType, filterActive, filterPublic]);

  const save = async (data) => {
    try {
      if (editing) {
        await axios.put(`${API}/${editing.id}`, data, { withCredentials: true });
      } else {
        await axios.post(API, data, { withCredentials: true });
      }
      setShowForm(false);
      setEditing(null);
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (c) => {
    if (!c.can_hard_delete) {
      alert(
        `"${c.name}" has donations, distributions, or reports attached. `
        + 'Permanent delete is disabled to protect your records. Archive it instead.'
      );
      return;
    }
    if (!window.confirm(
      `Permanently delete "${c.name}"?\n\n`
      + 'This campaign has no financial history so this is safe. This cannot be undone.'
    )) return;
    try {
      await axios.delete(`${API}/${c.id}`, { withCredentials: true });
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const archive = async (c) => {
    if (!window.confirm(
      `Archive "${c.name}"?\n\n`
      + 'It will be hidden from the public site and stop accepting new donations, '
      + 'but all records (donors, distributions, reports) stay intact and accessible.'
    )) return;
    try {
      await axios.post(`${API}/${c.id}/archive`, {}, { withCredentials: true });
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Archive failed'); }
  };

  const unarchive = async (c) => {
    try {
      await axios.post(`${API}/${c.id}/unarchive`, {}, { withCredentials: true });
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Restore failed'); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.post(`${API}/${c.id}/toggle-active`, {}, { withCredentials: true });
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Toggle failed'); }
  };

  const togglePublic = async (c) => {
    try {
      await axios.post(`${API}/${c.id}/toggle-public`, {}, { withCredentials: true });
      await loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || 'Toggle failed'); }
  };

  return (
    <div className="space-y-4" data-testid="campaign-manager-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0A0A0A]">Campaign Manager</h2>
          <p className="text-xs text-[#6B7280]">
            Create, activate, and track every fundraising campaign — crisis response, fundraisers, supply drives, and more.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadCampaigns}
                  className="p-2 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                  title="Refresh"
                  data-testid="refresh-campaigns-btn">
            <RefreshCw className="w-4 h-4 text-[#4B5563]" />
          </button>
          <button onClick={() => setShowTypes(true)}
                  className="text-sm px-3 py-2 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] flex items-center gap-1.5"
                  data-testid="manage-types-btn">
            <Tag className="w-4 h-4" /> Types
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
                  className="btn-primary text-sm flex items-center gap-1.5"
                  data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#6B7280]" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                className="text-sm px-2 py-1 border border-[#E5E7EB] rounded"
                data-testid="filter-type-select">
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}
                className="text-sm px-2 py-1 border border-[#E5E7EB] rounded"
                data-testid="filter-active-select">
          <option value="">Active + Inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <select value={filterPublic} onChange={(e) => setFilterPublic(e.target.value)}
                className="text-sm px-2 py-1 border border-[#E5E7EB] rounded"
                data-testid="filter-public-select">
          <option value="">Public + Private</option>
          <option value="public">Public only</option>
          <option value="private">Private only</option>
        </select>
        <label className="text-sm inline-flex items-center gap-1.5 cursor-pointer select-none pl-2 border-l border-[#E5E7EB]">
          <input type="checkbox" checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-[#BA1826]"
            data-testid="show-archived-toggle" />
          <Archive className="w-3.5 h-3.5 text-[#92400E]" />
          Show archived
        </label>
        <span className="text-xs text-[#6B7280] ml-auto" data-testid="filtered-count">
          Showing {filtered.length} of {campaigns.length}
        </span>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-10 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#BA1826]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E7EB] rounded-lg p-8 text-center"
             data-testid="empty-state">
          <AlertTriangle className="w-8 h-8 mx-auto text-[#9CA3AF] mb-2" />
          <p className="text-sm text-[#4B5563] mb-3">
            {campaigns.length === 0
              ? 'No campaigns yet. Create your first one — donations from that link will be tracked separately from general donations.'
              : 'No campaigns match your filters.'}
          </p>
          {campaigns.length === 0 && (
            <button onClick={() => { setEditing(null); setShowForm(true); }}
                    className="btn-primary text-sm inline-flex items-center gap-1.5"
                    data-testid="empty-new-btn">
              <Plus className="w-4 h-4" /> Create your first campaign
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="campaign-list">
          {filtered.map((c) => (
            <CampaignRow key={c.id} c={c} types={types}
              onEdit={(x) => { setEditing(x); setShowForm(true); }}
              onDelete={remove}
              onArchive={archive}
              onUnarchive={unarchive}
              onToggleActive={toggleActive}
              onTogglePublic={togglePublic}
              onShowLink={setShowLinkFor}
              onShowTools={setShowToolsFor} />
          ))}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <CampaignForm campaign={editing} types={types} countryPrograms={countryPrograms}
          onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />
      )}
      {showTypes && (
        <TypesManager types={types} onChanged={loadTypes} onClose={() => setShowTypes(false)} />
      )}
      {showLinkFor && (
        <LinkAndQrModal campaign={showLinkFor} onClose={() => setShowLinkFor(null)} />
      )}
      {showToolsFor && (
        <CampaignToolsModal campaign={showToolsFor}
          onClose={() => setShowToolsFor(null)}
          onUpdated={(updated) => {
            setShowToolsFor(updated);
            // Refresh the underlying list so metrics on the row reflect
            // any distribution changes the admin made inside the modal.
            loadCampaigns();
          }} />
      )}
    </div>
  );
};

export default CampaignManagerTab;
